import MongoTable from '../table/mongotable';
import React, { useState, useEffect, useMemo } from 'react';
import { useAxios } from '../../contexts/axios';
import { BACKEND_URL } from '../../configs';
import OvertimeDescription from '../table/overtimeDescription';
import TotalDescription from '../table/totalDescription';
import { styled } from '@mui/material/styles';
import DD2 from '../DD2';
import PrimaryButton from '../button/PrimaryButton';
import DatePickerInput from '../DatePickerInput';
import FlexRow from '../layout/FlexRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import TabPanel from '../tabs/tabPanel';
import { useMongoClustersMetadata } from '../../hooks/costing/useMongo';
import { useElasticDeploymentsMetadata } from '../../hooks/costing/useElastic';
import { useRedisInstancesMetadata } from '../../hooks/costing/useRedis';
import {
    Checkbox,
    FormControlLabel,
    LinearProgress,
} from '@mui/material';
const a11yProps = ({ index }) => {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
};

/** Sum each period column across rows (matches MongoTable total line for the graph). */
function computeUnitCostingPeriodTotals(rows) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return null;
    }
    const head = Object.keys(rows[0]);
    if (head.length < 2) {
        return null;
    }
    const totals = [];
    for (let c = 1; c < head.length; c++) {
        const colKey = head[c];
        let sum = 0;
        for (let r = 0; r < rows.length; r++) {
            const v = Number(rows[r][colKey]);
            sum += Number.isFinite(v) ? v : 0;
        }
        totals.push(sum);
    }
    return totals;
}

function formatDateForApi(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export default function UnitCostingTable() {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [finalData, setFinalData] = useState([]);
    const [totalData, setTotalData] = useState(null);
    const [ok, setOk] = useState(false);
    const authAxios = useAxios();
    const { data: mongoMappingData } = useMongoClustersMetadata();
    const { data: elasticMappingData } = useElasticDeploymentsMetadata();
    const { data: redisMappingData } = useRedisInstancesMetadata();
    const [metabaseMappingData, setMetabaseMappingData] = useState({});
    const [value, setValue] = useState(0);
    /** GRAPH tab: stack bars by group-by dimension (same chart as total columns). */
    const [showGroupBreakdown, setShowGroupBreakdown] = useState(false);

    const tools = [
        { name: 'Mongo', id: 'mongo' },
        { name: 'Elastic', id: 'elastic' },
        { name: 'Redis', id: 'redis' },
        { name: 'KafkaTopic', id: 'kafka_topic' },
        { name: 'KafkaCluster', id: 'kafka_cluster' },
        { name: 'Bigquery Analysis', id: 'bigquery' },
        { name: 'Bigquery Analysis (Metabase)', id: 'bigquery' },
    ];
    const periodicities = [
        { name: 'Daily', id: 'daily' },
        { name: 'Weekly', id: 'weekly' },
        { name: 'Monthly', id: 'monthly' },
    ];

    const verticalOptions = [
        { name: 'BM', id: 'BM' },
        { name: 'MARP', id: 'MARP' },
    ];

    var endDateMaxLimit = new Date();
    endDateMaxLimit.setDate(endDateMaxLimit.getDate() - 2);

    const [tool, setTool] = useState(tools[0]);
    const [groupBy, setGroupBy] = useState(null);
    const [periodicity, setPeriodicity] = useState(null);
    /** BM / MARP filter for Mongo and Redis when group-by is not Vertical (not Elastic). */
    const [vertical, setVertical] = useState(null);
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 2);
        return d;
    });
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 2);
        return d;
    });

    var groupbys = {
        Mongo: [
            { name: 'Cluster', id: 'cluster' },
            { name: 'Group', id: 'group_name' },
            { name: 'Vertical', id: 'vertical' },
            { name: 'Team', id: 'team' },
        ],
        Elastic: [
            { name: 'Deployment', id: 'deployment' },
            { name: 'Vertical', id: 'vertical' },
            { name: 'Team', id: 'team' },
        ],
        Redis: [
            { name: 'DataBase', id: 'database_name' },
            {name: 'Subscription', id: 'subscription_name'},
            { name: 'Vertical', id: 'vertical' },
            { name: 'Team', id: 'team' },
        ],
        KafkaTopic: [
            { name: 'Topic', id: 'topic' },
            { name: 'Vertical', id: 'vertical' },
            { name: 'Team', id: 'team' },
            { name: 'Owner', id: 'owner' },
        ],
        KafkaCluster: [
            { name: 'Cluster', id: 'cluster' },
            { name: 'Vertical', id: 'vertical' },
            { name: 'Team', id: 'team' },
            { name: 'Owner', id: 'owner' },
        ],
        'Bigquery Analysis': [
            { name: 'User Id', id: 'principal' },
            { name: 'Agent', id: 'agent' },
            { name: 'Team', id: 'team' },
        ],
        'Bigquery Analysis (Metabase)': [
            { name: 'Dashboard (Cost)', id: 'dashboard' },
            { name: 'Dashboard (Count)', id: 'dashboard' },
            { name: 'Question (Cost)', id: 'question' },
            { name: 'Question (Count)', id: 'question' },
        ],
    };

    const isInputValid = () => {
        if (!!!tool) {
            alert('Please select a tool');
            return false;
        }
        if (!!!groupBy) {
            alert('Please select a way to group by');
            return false;
        }
        if (!!!periodicity) {
            alert('Please select a periodicity for data');
            return false;
        }
        if (startDate > endDate) {
            alert('Please enter valid start and end date');
            return false;
        }
        const needsVerticalTool =
            tool.id === 'mongo' ||
            tool.id === 'redis' ||
            tool.id === 'kafka_topic' ||
            tool.id === 'kafka_cluster';
        const needsVerticalParam = needsVerticalTool && groupBy?.id !== 'vertical';
        if (needsVerticalParam && !vertical) {
            alert('Please select a vertical');
            return false;
        }
        return true;
    };

   
    const getUnitCostingData = async () => {
        if (!isInputValid() || loading) {
            return;
        }

        setLoading(true);
        try {
            const groupByParam =
                tool.name === 'Bigquery Analysis (Metabase)'
                    ? groupBy.name === 'Dashboard (Cost)'
                        ? 'dashboardCost'
                        : groupBy.name === 'Dashboard (Count)'
                        ? 'dashboardCount'
                        : groupBy.name === 'Question (Cost)'
                        ? 'questionCost'
                        : 'questionCount'
                    : groupBy.id;

            const needsVerticalTool =
                tool.id === 'mongo' ||
                tool.id === 'redis' ||
                tool.id === 'kafka_topic' ||
                tool.id === 'kafka_cluster';
            const appendVerticalParam =
                needsVerticalTool &&
                groupBy?.id !== 'vertical' &&
                vertical?.id;

            const params = new URLSearchParams({
                toolName: tool.id,
                groupBy: groupByParam,
                startDate: formatDateForApi(startDate),
                endDate: formatDateForApi(endDate),
                periodicity: periodicity?.id ?? 'none',
                view: 'overtime',
            });
            if (appendVerticalParam) {
                params.set('vertical', vertical.id);
            }

            await authAxios
                .get(`${BACKEND_URL}/unitcosting?${params.toString()}`)
                .then((response) => {
                var data=response.data;
                if(tool?.name === 'Bigquery Analysis (Metabase)'){
                    var tempData=[];
                    var keys=[]
                    if(data?.length>0){
                        keys=Object.keys(data[0]);
                        var temp=keys[keys.length-1];
                        keys.pop();
                        keys.unshift(temp);
                    }
                    for(var i=0; i<data?.length; i++){
                        var tempDatapoint={}
                        for(var j=0; j<keys.length; j++){
                            tempDatapoint[keys[j]]=data[i][keys[j]];
                        }
                        tempData.push(tempDatapoint);
                    }
                    data=tempData;
                }
                // MongoTable expects the group-by dimension as the first column.
                // Some APIs (e.g. kafka cluster/topic) return it at the end.
                if (Array.isArray(data) && data.length > 0 && groupBy?.id) {
                    data = data.map((row) => {
                        if (
                            !row ||
                            typeof row !== 'object' ||
                            !(groupBy.id in row)
                        ) {
                            return row;
                        }
                        const reorderedRow = { [groupBy.id]: row[groupBy.id] };
                        Object.keys(row).forEach((key) => {
                            if (key !== groupBy.id) {
                                reorderedRow[key] = row[key];
                            }
                        });
                        return reorderedRow;
                    });
                }
                setData(data);
                });
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(()=>{
        var finalList = []
        if(data?.length>0){
            if((groupBy.name).includes("Question")){
                for(var i=0; i<data.length; i++){
                    finalList.push(data[i]);
                    finalList[i]["question"]= finalList[i]["question"].split("__#Question#__:")[1];
                }
                setFinalData(finalList)
            }
            else{
                setFinalData(data);
            }
        }
    },[data])
    // use effect to update the group by options when the tool changes.
    useEffect(() => {
        /*if (tool.name === 'Bigquery Analysis (Metabase)') {
            getMetabaseData();
        }*/
        setGroupBy(groupbys[tool.name][0]);
        if (
            tool.id !== 'mongo' &&
            tool.id !== 'redis' &&
            tool.id !== 'kafka_topic' &&
            tool.id !== 'kafka_cluster'
        ) {
            setVertical(null);
        }
    }, [tool]);

    useEffect(() => {
        setData(null);
        setFinalData([]);
        setValue(0);
        setShowGroupBreakdown(false);
    }, [startDate, endDate, groupBy, periodicity, vertical]);

    const showVerticalPicker =
        (tool.id === 'mongo' ||
            tool.id === 'redis' ||
            tool.id === 'kafka_topic' ||
            tool.id === 'kafka_cluster') &&
        groupBy?.id !== 'vertical';

    const graphRows =
        Array.isArray(finalData) && finalData.length > 0 ? finalData : data;
    const graphTotalData = useMemo(() => {
        if (!graphRows?.length) {
            return null;
        }
        return computeUnitCostingPeriodTotals(graphRows);
    }, [graphRows]);

    const graphPeriodCount =
        graphRows?.length > 0 && graphRows[0]
            ? Math.max(0, Object.keys(graphRows[0]).length - 1)
            : 0;
    const graphChartReady =
        graphPeriodCount > 0 &&
        Array.isArray(graphTotalData) &&
        graphTotalData.length === graphPeriodCount;

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };
    //console.log(data)
    return (
        <>
            {!!loading ? (
                <Box sx={{ width: '100%', marginTop: '40vh' }}>
                    <LinearProgress />
                </Box>
            ) : (
                <>
                    <FlexRow>
                        <DD2
                            label={'Tool Name'}
                            emptyLabel={'Select a Tool Type'}
                            options={tools}
                            value={tool}
                            onChange={setTool}
                            displayProperty={'name'}
                            disabled={false}
                        />
                        <DD2
                            label={'Group By'}
                            emptyLabel={'Select a way to Group By'}
                            options={groupbys[tool.name]}
                            value={groupBy}
                            onChange={setGroupBy}
                            displayProperty={'name'}
                            disabled={!!!tool}
                        />

                        <DD2
                            label={'Periodicity'}
                            emptyLabel={'Select a periodicity'}
                            options={periodicities}
                            value={periodicity}
                            onChange={setPeriodicity}
                            displayProperty={'name'}
                            disabled={!!!groupBy}
                        />

                        {showVerticalPicker ? (
                            <DD2
                                label={'Vertical'}
                                emptyLabel={'Select a vertical'}
                                options={verticalOptions}
                                value={vertical}
                                onChange={setVertical}
                                displayProperty={'name'}
                                disabled={!!!groupBy}
                            />
                        ) : null}

                        <FlexRow />
                    </FlexRow>
                    <FlexRow>
                        <DatePickerInput
                            label={'Start Date'}
                            value={startDate}
                            onChange={setStartDate}
                            maxDate={endDate}
                            minDate={
                                groupBy?.name.includes('Dashboard')
                                    ? new Date('2023-04-12T00:00:00Z')
                                    : groupBy?.name.includes('Question')
                                    ? new Date('2023-05-02T00:00:00Z')
                                    : new Date('2023-01-01T00:00:00Z')
                            }
                        />
                        <DatePickerInput
                            label={'End Date'}
                            value={endDate}
                            onChange={setEndDate}
                            maxDate={endDateMaxLimit}
                            minDate={startDate}
                        />
                        <FlexRow></FlexRow>
                        <FlexRow></FlexRow>
                    </FlexRow>
                    <div className="mt-8">
                        <PrimaryButton
                            title={'$ Get Unit Costing Data'}
                            onClick={getUnitCostingData}
                        />
                    </div>
                    {!!data ? (
                        <Box sx={{ width: '100%', marginTop: '30px' }}>
                            <Box
                                sx={{ borderBottom: 1, borderColor: 'divider' }}
                            >
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    aria-label="basic tabs example"
                                >
                                    <Tab label="TABLE" {...a11yProps(0)} />
                                    <Tab label="GRAPH" {...a11yProps(1)} />
                                </Tabs>
                            </Box>
                            <TabPanel value={value} index={0}>
                                {finalData.length>0 ? tool?.id === 'mongo' ? (
                                    <MongoTable
                                        data={finalData}
                                        mappingData={mongoMappingData}
                                        groupBy={groupBy?.id}
                                        groupByName={groupBy?.name}
                                        periodicity={periodicity?.id}
                                        setTotalData={setTotalData}
                                        toolName={tool.id}
                                    />
                                ) : tool?.id === 'elastic' ? (
                                    <MongoTable
                                        data={finalData}
                                        mappingData={elasticMappingData}
                                        groupBy={groupBy?.id}
                                        groupByName={groupBy?.name}
                                        periodicity={periodicity?.id}
                                        setTotalData={setTotalData}
                                        toolName={tool.id}
                                    />
                                ) : tool?.id === 'redis' ? (
                                    <MongoTable
                                        data={finalData}
                                        mappingData={redisMappingData ?? []}
                                        groupBy={groupBy?.id}
                                        groupByName={groupBy?.name}
                                        periodicity={periodicity?.id}
                                        setTotalData={setTotalData}
                                        toolName={tool.id}
                                    />
                                ) : tool.id === 'bigquery' ? (
                                    <MongoTable
                                        data={finalData}
                                        mappingData={[]}
                                        groupBy={groupBy?.id}
                                        groupByName={groupBy?.name}
                                        periodicity={periodicity?.id}
                                        setTotalData={setTotalData}
                                        toolName={tool.id}
                                        startDate={formatDateForApi(startDate)}
                                        endDate={formatDateForApi(endDate)}
                                    />
                                ) : (
                                    <MongoTable
                                        data={finalData}
                                        mappingData={[]}
                                        groupBy={groupBy?.id}
                                        groupByName={groupBy?.name}
                                        periodicity={periodicity?.id}
                                        setTotalData={setTotalData}
                                        toolName={tool.id}
                                        startDate={formatDateForApi(startDate)}
                                        endDate={formatDateForApi(endDate)}
                                    />
                                ):<></>}
                            </TabPanel>
                            <TabPanel value={value} index={1}>
                                <div className="mt-8 min-w-[1500px] p-4">
                                    {graphChartReady ? (
                                        <>
                                            <FormControlLabel
                                                className="mb-4 block"
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            showGroupBreakdown
                                                        }
                                                        onChange={(e) =>
                                                            setShowGroupBreakdown(
                                                                e.target.checked
                                                            )
                                                        }
                                                        color="primary"
                                                    />
                                                }
                                                label={`Show cost breakdown by ${groupBy?.name || 'group'} (stacked columns)`}
                                            />
                                            <OvertimeDescription
                                                data={graphRows}
                                                groupBy={groupBy?.id}
                                                groupByName={groupBy?.name}
                                                periodicity={periodicity?.id}
                                                totalCost={graphTotalData}
                                                alwaysColumnTotal
                                                showServiceBreakdown={
                                                    showGroupBreakdown
                                                }
                                                enableBreakdownForDaily
                                                breakdownExcludeCluster={
                                                    false
                                                }
                                            />
                                        </>
                                    ) : (
                                        <Box sx={{ mt: 2 }}>
                                            Unable to build chart (no period
                                            columns in the response).
                                        </Box>
                                    )}
                                </div>
                            </TabPanel>
                        </Box>
                    ) : (
                        <></>
                    )}
                </>
            )}
        </>
    );
}
